/**
 * Der Verlauf eines Datensatzes als Zeitstrahl (REQ-26 §2.3) — Nachfolger von `AuditSidebar`
 * (`IwmSidebarAudit.razor`). Vorlage ist `AuditDrawer.tsx` der ChinaApp.
 *
 * Der Endpunkt `…/{viewId}/{id}/audit` kennt keine Seiten und liefert den ganzen Verlauf auf
 * einmal. Gefiltert und abgeschnitten wird deshalb im Client; die Fußleiste sagt, wie viel
 * davon zu sehen ist.
 */

import { useMemo, useRef, useState } from 'react';

import {
  Button,
  CheckBox,
  DateRangePicker,
  Drawer,
  DropDownList,
  EmptyState,
  ErrorState,
  FieldLabel,
  Spinner,
} from '@/components/ui';
import type { SelectOption } from '@/components/ui/types';
import type { EntityTypes } from '@/types/enums';

import { AuditEntry } from './AuditEntry';
import {
  auditKindLabels,
  emptyAuditFilter,
  filterAuditRecords,
  isAuditFilterActive,
  parseAuditEntries,
  type AuditKindFilter,
} from './auditDiff';
import { useAuditEntries } from './useAuditEntries';

/** So viele Einträge auf einmal; „Weitere anzeigen" legt dieselbe Menge nach. */
const PAGE_SIZE = 50;

const KIND_OPTIONS: SelectOption<AuditKindFilter>[] = [
  { value: 'all', text: 'Alle Arten' },
  { value: 'created', text: auditKindLabels.created },
  { value: 'updated', text: auditKindLabels.updated },
  { value: 'deleted', text: auditKindLabels.deleted },
];

interface DateRange {
  startDate: Date | null;
  endDate: Date | null;
}

const NO_RANGE: DateRange = { startDate: null, endDate: null };

export interface AuditDrawerProps {
  entity: EntityTypes;
  viewId: number;
  entryId: number;
  open: boolean;
  onClose(): void;
}

/** Technische Einzelheit für `ErrorState`: die Meldung des Dienstes samt Ursache. */
function describeError(error: unknown): string | undefined {
  if (!(error instanceof Error)) return typeof error === 'string' ? error : undefined;
  return error.cause instanceof Error ? `${error.message}\n${error.cause.message}` : error.message;
}

/** „{n} von {m} Änderungen", mit Filter „… (gefiltert aus {gesamt})". */
function countText(shown: number, filtered: number, total: number): string {
  const noun = filtered === 1 ? 'Änderung' : 'Änderungen';
  const text = `${String(shown)} von ${String(filtered)} ${noun}`;
  return filtered === total ? text : `${text} (gefiltert aus ${String(total)})`;
}

/**
 * Der Zustand hängt an der Sitzung: Geschlossen gibt es ihn nicht. Jedes Öffnen beginnt damit
 * ungefiltert und oben — ein Filter vom vorigen Datensatz wäre beim nächsten nicht zu erklären.
 */
export function AuditDrawer(props: AuditDrawerProps) {
  if (!props.open) return null;
  return <AuditDrawerSession {...props} />;
}

function AuditDrawerSession(props: AuditDrawerProps) {
  const { entity, entryId, viewId } = props;
  const [search, setSearch] = useState(emptyAuditFilter.search);
  const [searchFocused, setSearchFocused] = useState(false);
  const [kind, setKind] = useState<AuditKindFilter>(emptyAuditFilter.kind);
  const [range, setRange] = useState<DateRange>(NO_RANGE);
  const [limit, setLimit] = useState(PAGE_SIZE);
  const [showRaw, setShowRaw] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const close = () => {
    props.onClose();
  };
  const history = useAuditEntries({ entity, viewId, entryId, onClose: close });

  const records = useMemo(
    () => parseAuditEntries(history.entries, entity, history.fields),
    [entity, history.entries, history.fields],
  );
  const filter = useMemo(
    () => ({ search, kind, from: range.startDate, to: range.endDate }),
    [kind, range, search],
  );
  const filtered = useMemo(() => filterAuditRecords(records, filter), [filter, records]);
  const shown = filtered.slice(0, limit);
  const failed = history.error !== null && history.error !== undefined;

  // Jede Änderung am Filter beginnt wieder bei den ersten 50 Einträgen.
  const resetFilter = () => {
    setSearch(emptyAuditFilter.search);
    setKind(emptyAuditFilter.kind);
    setRange(NO_RANGE);
    setLimit(PAGE_SIZE);
  };

  const renderBody = () => {
    if (history.loading) {
      return (
        <div className="iwm-state-loading">
          <span className="iwm-state-spinner">
            <Spinner size={20} />
          </span>
          Verlauf wird geladen …
        </div>
      );
    }
    if (failed) {
      return (
        <ErrorState
          message="Der Verlauf konnte nicht geladen werden."
          detail={describeError(history.error)}
          onRetry={history.retry}
        />
      );
    }
    if (records.length === 0) {
      return (
        <EmptyState
          iconClass="fa-solid fa-clock-rotate-left"
          title="Kein Verlauf"
          description="Für diesen Datensatz sind keine Änderungen verzeichnet."
        />
      );
    }
    if (filtered.length === 0) {
      return (
        <EmptyState
          iconClass="fa-solid fa-magnifying-glass"
          title="Nichts gefunden"
          description="Zu dieser Auswahl gibt es keinen Eintrag."
          action={
            isAuditFilterActive(filter) ? (
              <Button content="Filter zurücksetzen" onClick={resetFilter} />
            ) : undefined
          }
        />
      );
    }
    return (
      <ol className="iwm-audit-timeline" aria-label="Änderungen">
        {shown.map((record) => (
          <AuditEntry key={record.id} record={record} showRaw={showRaw} />
        ))}
      </ol>
    );
  };

  const footer = (
    <>
      {history.loading || failed || records.length === 0 ? null : (
        <span className="iwm-audit-summary">
          {countText(shown.length, filtered.length, records.length)}
        </span>
      )}
      {filtered.length > shown.length ? (
        <Button
          content="Weitere anzeigen"
          onClick={() => {
            setLimit((current) => current + PAGE_SIZE);
          }}
        />
      ) : null}
      <Button primary content="Cancel" onClick={close} />
    </>
  );

  return (
    <Drawer
      open
      onClose={close}
      id="iwm-audit-drawer"
      className="iwm-audit-drawer"
      title="Audit"
      description={`Datensatz ${String(entryId)}`}
      width="900px"
      initialFocusRef={searchRef}
      footer={footer}
    >
      <div className="iwm-audit-filters">
        <FieldLabel label="Suche" focused={searchFocused} className="iwm-audit-search">
          {({ id }) => (
            <>
              <input
                ref={searchRef}
                id={id}
                type="search"
                className="e-input"
                placeholder="Feld, Benutzer oder Wert"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setLimit(PAGE_SIZE);
                }}
                onFocus={() => {
                  setSearchFocused(true);
                }}
                onBlur={() => {
                  setSearchFocused(false);
                }}
              />
              <span
                className="e-input-group-icon e-search-icon e-icons fa-solid fa-magnifying-glass"
                aria-hidden="true"
              />
            </>
          )}
        </FieldLabel>
        <DropDownList
          label="Art"
          items={KIND_OPTIONS}
          fields={{ value: 'value', text: 'text' }}
          value={kind}
          onChange={(value) => {
            setKind(value ?? emptyAuditFilter.kind);
            setLimit(PAGE_SIZE);
          }}
        />
        {/* `DateRangePicker` hat keine eigene Beschriftung; die Hülle folgt `FieldLabel`. Der
            Klick im Feld wird nicht an das <label> weitergereicht — sonst schaltete der
            Kalenderknopf das Popup zweimal um und es bliebe zu. */}
        <label className="iwm-field iwm-audit-range">
          <span className="iwm-field-label">Zeitraum</span>
          <span
            className="iwm-audit-range-field"
            onClick={(event) => {
              event.preventDefault();
            }}
          >
            <DateRangePicker
              startDate={range.startDate}
              endDate={range.endDate}
              showClearButton
              onChange={(value) => {
                setRange(value);
                setLimit(PAGE_SIZE);
              }}
            />
          </span>
        </label>
        <CheckBox
          className="iwm-audit-raw-toggle"
          label="Rohdaten anzeigen"
          checked={showRaw}
          onChange={setShowRaw}
        />
      </div>
      {renderBody()}
    </Drawer>
  );
}

export default AuditDrawer;
